import { Component, effect, signal, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ReportField, Report, ReportType } from '@modules/reports/Domain/reports.model';
import { ReportFacade } from '@modules/reports/Services/reports.facade';
import * as ReportFields from '@modules/reports/Services/config/report-fields.config';
import { NgForOf, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CollectorModel } from '@admin/modules/collectors/model/collectors.model';
import { CampaignModel } from '@modules/campaigns/models/campaign.model';
import { TreeSelectComponent } from '@shared/components/tree-select/tree-select.component';
import { TreeNode } from '@shared/components/tree-select/Domain/treeNode.model';
import { OrderCampaignsService } from '@shared/services/orderCampaigns.service';
import { Portfolio } from '@modules/operations/Domain/operation.model';
import { PipeModule } from '@shared/pipes/pipe.module';
import { ReportService } from '@modules/reports/Services/reports.service';
import { debounceTime } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { ReportDescriptions } from '@modules/reports/Domain/report-description.config';
import { SelectionMode } from '@shared/components/tree-select/Domain/selectionMode.enum';
import { ChipModule } from 'primeng/chip';
import { DividerModule } from 'primeng/divider';
import { MultiSelectModule } from 'primeng/multiselect';

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  standalone: true,
  imports: [
    NgIf,
    ReactiveFormsModule,
    NgForOf,
    NgSwitch,
    NgSwitchCase,
    InputTextModule,
    DropdownModule,
    CalendarModule,
    TreeSelectComponent,
    FormsModule,
    PipeModule,
    TranslateModule,
    TooltipModule,
    ChipModule,
    DividerModule,
    MultiSelectModule,
  ],
})
export class ReportsComponent implements OnInit {
  report = signal<Report | null>(null);
  fields = signal<ReportField[]>([]);
  noDataFilters = signal(false);
  fieldsOptions = signal<Record<string, any[]>>({});

  public safeUrl: SafeResourceUrl | null = null;

  filtersForm!: FormGroup;
  collectors: CollectorModel[] = [];
  campaigns: CampaignModel[] = [];
  campaignNodes: TreeNode[] = [];
  portfolios: Portfolio[] = [];
  operationStatus: any[] = []; //TODO

  protected readonly SelectionMode = SelectionMode;
  protected readonly ReportType = ReportType;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private orderCampaignsService: OrderCampaignsService,
    private service: ReportService,
    public facade: ReportFacade,
  ) {
    effect(() => {
      const url = this.facade.reportUrl();
      this.safeUrl = url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
    });
  }

  private isPanelReportType(type: ReportType) {
    return type === ReportType.PANEL;
  }

  ngOnInit() {
    // subscribe to route param changes (changes the type of report)
    this.route.paramMap.subscribe((params) => {
      const rawTypeParam = params.get('type');
      const typeParam = rawTypeParam?.toUpperCase() ?? null;

      // redirect on invalid type or legacy panel_v2 url
      if (!this.isValidReportType(typeParam) || typeParam === ReportType.PANEL_V2) {
        this.router.navigate(['/reports', 'panel'], {
          queryParamsHandling: 'preserve',
        });
        return;
      }

      const reportType = typeParam as ReportType;
      const tenant = this.route.snapshot.queryParamMap.get('tenant');

      this.collectors = this.route.snapshot.data['collectors'] || [];
      this.campaigns = this.route.snapshot.data['campaigns'] || [];
      this.operationStatus =
        this.route.snapshot.data['operationStatus']?.data?.operationStatus || [];
      this.portfolios = this.route.snapshot.data['portfolios'] || [];

      //Filtrar borradores
      this.campaigns = this.campaigns.filter((campaign) => campaign.status !== 'draft');
      this.campaignNodes = this.orderCampaignsService.parseCampaignsToTree(this.campaigns);

      this.report.set({
        type: reportType,
        title: reportType,
        criteria: { tenantId: tenant },
        description: ReportDescriptions[reportType],
      });

      this.loadFields();

      if (reportType === ReportType.OUTBOUND_CALLS) {
        this.facade.fetchPhoneCallAgents().subscribe(() => {
          this.populateDropdownOptionsSignal();
        });
      } else {
        this.populateDropdownOptionsSignal();
      }

      this.filtersForm = this.service.buildForm(
        this.fields(),
        this.report()?.criteria,
        this.collectors,
        this.portfolios,
        this.operationStatus,
      );
      this.setRequiredDropdownDefaults();
      this.initializeCriteriaWithDefaults();

      this.filtersForm.valueChanges.pipe(debounceTime(300)).subscribe(() => {
        if (this.report()?.type === ReportType.DEBT_AGING) {
          const startDate = this.filtersForm.get('startDate')?.value;
          const endDate = this.filtersForm.get('endDate')?.value;

          if (startDate && !endDate) {
            const prevMonth = new Date(startDate);
            prevMonth.setMonth(prevMonth.getMonth() - 1);
            this.filtersForm.get('endDate')?.setValue(prevMonth, { emitEvent: false });
          }
        }

        const hasIncompleteDateRange = this.fields()
          .filter((f) => f.type === 'dateRange')
          .some((f) => {
            const value = this.filtersForm.get(f.name)?.value;
            return Array.isArray(value) && value[0] && !value[1];
          });

        if (hasIncompleteDateRange) return;

        if (this.filtersForm.valid) {
          this.noDataFilters.set(false);
          this.applyFormToReport();
        } else {
          this.noDataFilters.set(true);
        }
      });
    });
  }

  applyFormToReport() {
    const reportValue = this.report();
    if (!reportValue || !this.filtersForm) return;

    const mappedCriteria = this.service.mapFormValuesToCriteria(
      this.filtersForm.value,
      this.fields(),
    );

    reportValue.criteria = { ...reportValue.criteria, ...mappedCriteria };
    this.facade.loadReport(reportValue);
  }

  private initializeCriteriaWithDefaults() {
    const reportValue = this.report();
    if (!reportValue || !this.filtersForm) return;

    this.updateReportFromForm();
  }

  private isValidReportType(type: string | null): type is ReportType {
    return type !== null && Object.values(ReportType).includes(type as ReportType);
  }

  loadFields() {
    switch (this.report()!.type) {
      case ReportType.PANEL:
        this.fields.set(ReportFields.PanelReportV2Fields);
        break;
      case ReportType.CLIENTS:
        this.fields.set(ReportFields.ClientsReportFields);
        break;
      case ReportType.COLLECTION:
        this.fields.set(ReportFields.CollectionPeriodReportFields);
        break;
      case ReportType.PORTFOLIOS:
        this.fields.set(ReportFields.GlobalPortfolioReportFields);
        break;
      case ReportType.COLLECTORS:
        this.fields.set(ReportFields.CollectorsReportFields);
        break;
      case ReportType.NOTIFICATIONS:
        this.fields.set(ReportFields.NotificationsReportFields);
        break;
      case ReportType.OUTBOUND_CALLS:
        this.fields.set(ReportFields.OutboundCallsReportFields);
        break;
      case ReportType.COLLECTION_BY_PERIOD:
        this.fields.set(ReportFields.CollectionMonthReportFields);
        break;
      case ReportType.CAMPAIGNS_BY_MONTH:
        this.fields.set(ReportFields.CampaignsByMonthReportFields);
        break;
      case ReportType.DUE_DATE_PERIOD:
        this.fields.set(ReportFields.DueDatePeriodReportFields);
        break;
      case ReportType.DEBT_AGING:
        this.fields.set(ReportFields.DebtAgingReportFields);
        break;
      default:
        this.fields.set([]);
    }
  }

  onTreeSelectChange(selected: TreeNode[], fieldName: string) {
    if (this.filtersForm.get(fieldName)) {
      this.filtersForm.get(fieldName)!.setValue(selected[0].key);
      this.noDataFilters.set(false);
    }
  }

  private updateReportFromForm() {
    const reportValue = this.report();
    if (!reportValue) return;

    const mappedCriteria = this.service.mapFormValuesToCriteria(
      this.filtersForm.value,
      this.fields(),
    );

    reportValue.criteria = { ...reportValue.criteria, ...mappedCriteria };
    this.facade.loadReport(reportValue);
  }

  private populateDropdownOptionsSignal() {
    const agents =
      this.report()?.type === ReportType.OUTBOUND_CALLS ? this.facade.phoneCallAgents() : [];

    const options = this.service.getDropdownOptions(
      this.fields(),
      this.collectors,
      this.portfolios,
      this.operationStatus,
      agents,
    );

    this.fieldsOptions.set(options);
  }

  getFirstLeafKey(nodes: TreeNode[]): string | null {
    for (const node of nodes) {
      if (node.leaf) {
        const campaign = this.campaigns.find((c) => c.id === node.key);
        if (campaign?.status === 'published') {
          return node.key;
        }
      }

      if (node.children?.length) {
        const leafKey = this.getFirstLeafKey(node.children);
        if (leafKey) return leafKey;
      }
    }

    // fallback: no active campaign found
    return null;
  }

  resetFilters() {
    if (!this.filtersForm || !this.fields().length) return;

    this.fields().forEach((field) => {
      if (!this.filtersForm.contains(field.name)) return;

      if (field.required) {
        if (field.name === 'campaignId' && this.campaignNodes.length) {
          this.filtersForm.get(field.name)?.setValue([this.getFirstLeafKey(this.campaignNodes)]);
        } else if (field.type === 'dropdown') {
          const options = this.fieldsOptions()[field.name] || [];
          const firstValue = options.length ? options[0].value : null;
          this.filtersForm.get(field.name)?.setValue(firstValue);
        } else if (field.type === 'date') {
          this.filtersForm
            .get(field.name)
            ?.setValue(
              field.dateView === 'month'
                ? new Date(new Date().getFullYear(), new Date().getMonth(), 1)
                : new Date(),
            );
        }
      } else {
        this.filtersForm.get(field.name)?.reset();
      }
    });

    this.noDataFilters.set(false);
    this.applyFormToReport();
  }

  private setRequiredDropdownDefaults() {
    const options = this.fieldsOptions();

    //TODO: better option is to init value on the form builder IMPORTANT in future (put it in config const)
    this.fields().forEach((field) => {
      if (field.type === 'dropdown' && field.required) {
        const control = this.filtersForm.get(field.name);
        let firstValue = options[field.name]?.[0]?.value ?? null;
        const reportValue = this.report();

        if (field.name === 'months') {
          firstValue = 6;
          control!.setValue(firstValue, { emitEvent: false });
          return;
        }
        if (
          field.name === 'collectorId' &&
          reportValue &&
          this.isPanelReportType(reportValue.type)
        ) {
          if (!this.haveSameCurrency(this.collectors)) {
            control!.setValue(this.collectors[0].id, { emitEvent: false });
            return;
          } else {
            control!.setValue(null, { emitEvent: false });
            return;
          }
        }

        if (control && (control.value === null || control.value === undefined)) {
          if (firstValue !== null) {
            control.setValue(firstValue, { emitEvent: false });
          }
        }
      }

      if (field.name === 'campaignId' && field.required && this.campaignNodes.length) {
        const control = this.filtersForm.get(field.name);
        if (control && (!control.value || !control.value.length)) {
          control.setValue([this.getFirstLeafKey(this.campaignNodes)], { emitEvent: false });
        }
      }

      if (field.name === 'startDate' && field.dateView === 'month' && field.required) {
        const control = this.filtersForm.get(field.name);
        if (control) {
          const today = new Date();
          const isDebtAging = field.label === 'analysisMonth';
          const monthOffset = isDebtAging ? 0 : -1;
          const defaultDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
          control.setValue(defaultDate, { emitEvent: false });

          if (isDebtAging) {
            const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            this.filtersForm.get('endDate')?.setValue(prevMonth, { emitEvent: false });
          }
        }
      }

      if (field.label === 'dateNotification' && field.required) {
        const control = this.filtersForm.get(field.name);
        if (control) {
          control.setValue(new Date(), { emitEvent: false });
        }
      }
    });
  }

  haveSameCurrency(collectors: { currency?: string | null }[]): boolean {
    const currencies = collectors.map((c) => c.currency ?? null);
    return new Set(currencies).size === 1;
  }
}
